import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { BASE_URL } from '../../../utils/environment'
import GET_API_KEY from '../../../utils/getApiKey'
import './styles.scss'

/*
 * Help for clinicians (Header's Help button; replaced the Freshdesk launcher, Barbara 2026-10-19). Quick answers for
 * the screen they're on and the user guide; Report a problem opens the same Freshdesk ticket form, pre-filled; Send
 * feedback and Share your story are saved by the site (POST api/feedback) and emailed to our team. Stories: any honest
 * story, with a star rating; with permission to publish and our team's approval, the clinician gets a free month
 * (FTC: the thank-you can't depend on the story being positive, and it's disclosed when published).
 */
const GUIDE = 'https://smartyearslearning.com/user-guide/'

const ANSWERS = {
    picker: [
        ['How do I start a session?', 'Tap up to 4 students, then Start. You’ll choose the activity next, and each student gets their own turns, scores and reports.'],
        ['How do I add a student?', 'Tap Add a student. Once they’re saved, they show up here, ready to pick.'],
        ['Can I run a session with more than 4 students?', 'Not at once: groups are up to 4 so everyone gets enough turns. Run a second group right after.'],
        ['Where are my students’ reports?', 'Tap the chart button at the top to go to your students on the website. Each student’s Progress page has their reports.'],
    ],
    activities: [
        ['How do I choose an activity?', 'Search or filter by skill, language or grade, then tap an activity. Its settings open first so you can pick the targets.'],
        ['How do I change the students in this session?', 'Tap the house button at the top to go back to your students, and pick a new group.'],
    ],
    game: [
        ['How do I score each try?', 'Use the buttons on each student’s card: correct, approximation or wrong. Each tap goes straight into their report.'],
        ['How do I end the session?', 'Tap the house button at the top. Everything is saved as you go, so there’s nothing else to do.'],
        ['Where do I write session notes?', 'Tap the notes button at the top. Notes are saved to each student’s Notes tab.'],
    ],
}

const TOPICS = ['Activities', 'Progress reports', 'Home practice', 'Students and goals', 'Something else']

function post(body) {
    return fetch(BASE_URL + 'feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: 'Bearer ' + GET_API_KEY() },
        body: JSON.stringify(body),
    }).then((r) => {
        if (!r.ok) {
            throw new Error(String(r.status))
        }
        return r.json()
    })
}

export default function HelpPanel({ open, onClose, user }) {
    const location = useLocation()
    const [view, setView] = useState('menu')
    const [sending, setSending] = useState(false)
    const [error, setError] = useState('')
    const [done, setDone] = useState('')
    const [feedback, setFeedback] = useState({ topic: TOPICS[0], message: '' })
    const [story, setStory] = useState({ rating: 0, message: '', display: 'initial', title: '', workplace: '', publish: false })
    const panelRef = useRef(null)

    const screen = location.pathname === '/' ? 'picker' : location.pathname === '/select-activity' ? 'activities' : 'game'

    useEffect(() => {
        if (open) {
            setView('menu')
            setError('')
            setDone('')
            setTimeout(() => panelRef.current && panelRef.current.focus(), 30)
        }
    }, [open])

    useEffect(() => {
        if (!open) return undefined
        const onKey = (e) => e.key === 'Escape' && onClose()
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open, onClose])

    if (!open) return null

    const reportProblem = () => {
        const name = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : ''
        if (typeof window.FreshworksWidget === 'function') {
            window.FreshworksWidget('identify', 'ticketForm', { name, email: (user && user.email) || '' })
            window.FreshworksWidget('open', 'ticketForm')
            onClose()
        } else {
            setView('problem-mail')
        }
    }

    const send = (body, thanks) => {
        setSending(true)
        setError('')
        post({ ...body, page: location.pathname })
            .then(() => {
                setDone(thanks)
                setView('done')
            })
            .catch(() => setError('It couldn’t be sent. Please try again in a moment.'))
            .finally(() => setSending(false))
    }

    const sendFeedback = (e) => {
        e.preventDefault()
        if (feedback.message.trim().length < 5) {
            setError('Tell us a little more first.')
            return
        }
        send({ kind: 'feedback', topic: feedback.topic, message: feedback.message.trim() }, 'Thank you! Our team reads every message.')
    }

    const sendStory = (e) => {
        e.preventDefault()
        if (!story.rating) {
            setError('Choose a star rating first.')
            return
        }
        if (story.message.trim().length < 20) {
            setError('Tell us a little more: a sentence or two is perfect.')
            return
        }
        send(
            { kind: 'story', rating: story.rating, message: story.message.trim(), display: story.display, title: story.title.trim(), workplace: story.workplace.trim(), publish: story.publish },
            story.publish
                ? 'Thank you for sharing! We’ll read your story and email you when your free month is added.'
                : 'Thank you for sharing! It means a lot to our small team.'
        )
    }

    const back = (
        <button type="button" className="hp-back" onClick={() => { setView('menu'); setError('') }}>
            <i className="fas fa-arrow-left" aria-hidden="true"></i> Back
        </button>
    )

    return (
        <div className="hp-layer" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <aside className="hp-panel" role="dialog" aria-modal="true" aria-labelledby="hp-title" tabIndex={-1} ref={panelRef}>
                <div className="hp-top">
                    <h2 id="hp-title">{view === 'story' ? 'Share your story' : view === 'feedback' ? 'Send feedback' : 'How can we help?'}</h2>
                    <button type="button" className="hp-x" aria-label="Close help" onClick={onClose}>
                        <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                </div>

                {view === 'menu' && (
                    <div className="hp-body">
                        <h3>Quick answers</h3>
                        <div className="hp-faq">
                            {ANSWERS[screen].map(([q, a]) => (
                                <details key={q}>
                                    <summary>{q}</summary>
                                    <p>{a}</p>
                                </details>
                            ))}
                        </div>
                        <a className="hp-link" href={GUIDE} target="_blank" rel="noopener noreferrer">
                            <i className="fas fa-book-open" aria-hidden="true"></i> Open the user guide
                        </a>

                        <h3>Talk to us</h3>
                        <button type="button" className="hp-row" onClick={reportProblem}>
                            <span className="hp-ic is-coral"><i className="fas fa-life-ring" aria-hidden="true"></i></span>
                            <span><b>Report a problem</b><small>Something not working? Our support team will get back to you.</small></span>
                        </button>
                        <button type="button" className="hp-row" onClick={() => setView('feedback')}>
                            <span className="hp-ic is-blue"><i className="fas fa-lightbulb" aria-hidden="true"></i></span>
                            <span><b>Send feedback or an idea</b><small>Tell us what to improve or what you’d love to see.</small></span>
                        </button>
                        <button type="button" className="hp-row" onClick={() => setView('story')}>
                            <span className="hp-ic is-yellow"><i className="fas fa-star" aria-hidden="true"></i></span>
                            <span><b>Share your story</b><small>How has Smarty Ears Online worked for you? Get a free month as a thank-you.</small></span>
                        </button>
                    </div>
                )}

                {view === 'problem-mail' && (
                    <div className="hp-body">
                        {back}
                        <p className="hp-lead">Email our support team and we’ll get back to you:</p>
                        <a className="hp-btn" href="mailto:support@smartyearsapps.com?subject=Smarty%20Ears%20Online%20problem">support@smartyearsapps.com</a>
                    </div>
                )}

                {view === 'feedback' && (
                    <form className="hp-body" onSubmit={sendFeedback}>
                        {back}
                        <label className="hp-field">
                            <span>What’s it about?</span>
                            <select value={feedback.topic} onChange={(e) => setFeedback({ ...feedback, topic: e.target.value })}>
                                {TOPICS.map((t) => (
                                    <option key={t}>{t}</option>
                                ))}
                            </select>
                        </label>
                        <label className="hp-field">
                            <span>Your feedback or idea</span>
                            <textarea rows="6" maxLength="3000" value={feedback.message} onChange={(e) => setFeedback({ ...feedback, message: e.target.value })} placeholder="What would make Smarty Ears Online better for you?" />
                        </label>
                        {error && <p className="hp-error" role="alert">{error}</p>}
                        <button type="submit" className="hp-btn" disabled={sending}>{sending ? 'Sending…' : 'Send feedback'}</button>
                    </form>
                )}

                {view === 'story' && (
                    <form className="hp-body" onSubmit={sendStory}>
                        {back}
                        <p className="hp-lead">Tell us honestly how Smarty Ears Online has worked for you or your students. Good, bad or in between, every story helps.</p>
                        <div className="hp-field">
                            <span id="hp-stars-label">Your rating</span>
                            <div className="hp-stars" role="radiogroup" aria-labelledby="hp-stars-label">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button type="button" key={n} role="radio" aria-checked={story.rating === n} aria-label={`${n} star${n > 1 ? 's' : ''}`}
                                        className={n <= story.rating ? 'is-on' : ''} onClick={() => setStory({ ...story, rating: n })}>
                                        <i className="fas fa-star" aria-hidden="true"></i>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <label className="hp-field">
                            <span>Your story</span>
                            <textarea rows="5" maxLength="3000" value={story.message} onChange={(e) => setStory({ ...story, message: e.target.value })}
                                placeholder="What has changed for you or your students? A sentence or two is perfect." />
                        </label>
                        <div className="hp-field">
                            <span>If we share it, show my name as</span>
                            <div className="hp-choice">
                                {[['full', 'Full name'], ['initial', 'First name and last initial'], ['anonymous', 'Don’t show my name']].map(([v, l]) => (
                                    <label key={v}>
                                        <input type="radio" name="hp-display" value={v} checked={story.display === v} onChange={() => setStory({ ...story, display: v })} /> {l}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="hp-two">
                            <label className="hp-field">
                                <span>Your role <small>optional</small></span>
                                <input type="text" maxLength="60" value={story.title} onChange={(e) => setStory({ ...story, title: e.target.value })} placeholder="e.g. School SLP" />
                            </label>
                            <label className="hp-field">
                                <span>Where you work <small>optional</small></span>
                                <input type="text" maxLength="120" value={story.workplace} onChange={(e) => setStory({ ...story, workplace: e.target.value })} placeholder="e.g. a district in Texas" />
                            </label>
                        </div>
                        <label className="hp-check">
                            <input type="checkbox" checked={story.publish} onChange={(e) => setStory({ ...story, publish: e.target.checked })} />
                            <span>Smarty Ears may share my words on its website and social media, possibly shortened.</span>
                        </label>
                        <p className="hp-note">
                            <i className="fas fa-gift" aria-hidden="true"></i> As a thank-you for an honest story we can share, we’ll add a free month to your plan once our team has read it. We never ask for only positive reviews, and if we publish a story we say it came with a thank-you.
                        </p>
                        {error && <p className="hp-error" role="alert">{error}</p>}
                        <button type="submit" className="hp-btn" disabled={sending}>{sending ? 'Sending…' : 'Share my story'}</button>
                    </form>
                )}

                {view === 'done' && (
                    <div className="hp-body hp-done">
                        <img src="/assets/images/student-home/clever-heart.png" alt="" width="110" height="110" />
                        <p className="hp-lead">{done}</p>
                        <button type="button" className="hp-btn is-ghost" onClick={onClose}>Close</button>
                    </div>
                )}
            </aside>
        </div>
    )
}
